/**
 * Nightly Drive auto-backup of the agency's AgencyLens config.
 *
 * Browser-side: gathers IndexedDB records, wraps them in a JSON envelope,
 * uploads to the buyer's own Google Drive via the `drive.file` scope (which
 * only grants access to files our app creates — see Privacy Policy section 5).
 *
 * The connection records inside the bundle are already encrypted at the
 * IndexedDB level. We don't add a second encryption layer here for
 * simplicity — the bundle is "encrypted contents inside a JSON wrapper".
 * The wrapper is uploaded as-is; only someone with both Drive access AND
 * the buyer's per-buyer DEK can read the contents.
 */

import type { EncryptedBlob } from "@agencylens/shared";

export const DRIVE_SCOPE = "https://www.googleapis.com/auth/drive.file";
const FOLDER_NAME = "AgencyLens";
const FOLDER_MIME = "application/vnd.google-apps.folder";
const BACKUP_MIME = "application/json";

export class DriveBackupError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
  ) {
    super(message);
    this.name = "DriveBackupError";
  }
}

/* -------------------------------------------------- bundle format */

export const BACKUP_VERSION = 1 as const;

export interface BackupBundle {
  version: typeof BACKUP_VERSION;
  app: "agencylens";
  /** Source machine fingerprint (browser user-agent slug + locale) for the
   *  restore UI; not used for anything programmatic. */
  source?: string;
  createdAt: string;
  /** Buyer's email — informational only; restore doesn't require it match. */
  ownerEmail?: string;
  /** Per-IndexedDB-key payload. Values are the raw IDB records, which are
   *  themselves typed (ConnectionRecord with EncryptedBlob fields, etc.). */
  records: Record<string, unknown>;
}

export interface BundleInput {
  records: Record<string, unknown>;
  ownerEmail?: string;
  source?: string;
}

export function buildBundle(input: BundleInput): BackupBundle {
  return {
    version: BACKUP_VERSION,
    app: "agencylens",
    createdAt: new Date().toISOString(),
    ...(input.ownerEmail ? { ownerEmail: input.ownerEmail } : {}),
    ...(input.source ? { source: input.source } : {}),
    records: input.records,
  };
}

/* -------------------------------------------------- Drive API helpers */

async function driveGet<T>(accessToken: string, pathAndQuery: string): Promise<T> {
  const url = `https://www.googleapis.com${pathAndQuery}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return readJsonOrThrow<T>(res, `GET ${pathAndQuery}`);
}

async function readJsonOrThrow<T>(res: Response, label: string): Promise<T> {
  const text = await res.text();
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new DriveBackupError(
      `${label} returned non-JSON ${res.status}: ${text.slice(0, 200)}`,
      res.status,
    );
  }
  if (!res.ok) {
    const e = (data as { error?: { message?: string } })?.error?.message ?? `HTTP ${res.status}`;
    throw new DriveBackupError(`${label}: ${e}`, res.status);
  }
  return data as T;
}

/* -------------------------------------------------- folder management */

interface DriveFile {
  id: string;
  name: string;
  mimeType?: string;
  createdTime?: string;
  modifiedTime?: string;
  size?: string;
}

interface DriveFileList {
  files?: DriveFile[];
}

/**
 * Find or create the AgencyLens folder in the user's Drive root.
 */
export async function ensureBackupFolder(accessToken: string): Promise<string> {
  const q = encodeURIComponent(
    `mimeType='${FOLDER_MIME}' and name='${FOLDER_NAME}' and 'root' in parents and trashed=false`,
  );
  const found = await driveGet<DriveFileList>(
    accessToken,
    `/drive/v3/files?q=${q}&fields=files(id,name)&spaces=drive`,
  );
  const existing = found.files?.[0]?.id;
  if (existing) return existing;

  // Create it
  const created = await driveCreateMetadata(accessToken, {
    name: FOLDER_NAME,
    mimeType: FOLDER_MIME,
    parents: ["root"],
  });
  return created.id;
}

async function driveCreateMetadata(
  accessToken: string,
  metadata: { name: string; mimeType: string; parents: string[] },
): Promise<DriveFile> {
  const res = await fetch("https://www.googleapis.com/drive/v3/files?fields=id,name", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(metadata),
  });
  return readJsonOrThrow<DriveFile>(res, "POST /drive/v3/files (folder)");
}

/* -------------------------------------------------- upload + list + download */

export interface BackupFile {
  id: string;
  name: string;
  createdAt?: string;
  modifiedAt?: string;
  sizeBytes?: number;
}

export async function uploadBackup(
  accessToken: string,
  folderId: string,
  bundle: BackupBundle,
  fileName = defaultBackupName(),
): Promise<BackupFile> {
  // multipart/related upload: metadata JSON then content JSON
  const boundary = `--AgencyLensBoundary${Date.now()}`;
  const metadata = {
    name: fileName,
    parents: [folderId],
    mimeType: BACKUP_MIME,
  };
  const bodyParts = [
    `--${boundary}`,
    "Content-Type: application/json; charset=UTF-8",
    "",
    JSON.stringify(metadata),
    `--${boundary}`,
    `Content-Type: ${BACKUP_MIME}; charset=UTF-8`,
    "",
    JSON.stringify(bundle, null, 2),
    `--${boundary}--`,
    "",
  ];
  const body = bodyParts.join("\r\n");

  const res = await fetch(
    "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,createdTime,modifiedTime,size",
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": `multipart/related; boundary=${boundary}`,
      },
      body,
    },
  );
  const created = await readJsonOrThrow<DriveFile>(res, "POST /upload/drive/v3/files");
  return toBackupFile(created);
}

export async function listBackups(
  accessToken: string,
  folderId: string,
): Promise<BackupFile[]> {
  const q = encodeURIComponent(`'${folderId}' in parents and trashed=false`);
  const fields = encodeURIComponent("files(id,name,createdTime,modifiedTime,size),nextPageToken");
  const data = await driveGet<DriveFileList>(
    accessToken,
    `/drive/v3/files?q=${q}&fields=${fields}&orderBy=modifiedTime desc&pageSize=50`,
  );
  return (data.files ?? []).map(toBackupFile);
}

export async function downloadBackup(
  accessToken: string,
  fileId: string,
): Promise<BackupBundle> {
  const res = await fetch(
    `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
    { headers: { Authorization: `Bearer ${accessToken}` } },
  );
  const text = await res.text();
  if (!res.ok) {
    throw new DriveBackupError(
      `GET /drive/v3/files/{id}?alt=media: ${res.status} ${text.slice(0, 200)}`,
      res.status,
    );
  }
  let bundle: BackupBundle;
  try {
    bundle = JSON.parse(text) as BackupBundle;
  } catch {
    throw new DriveBackupError("Downloaded backup is not valid JSON.");
  }
  if (bundle.app !== "agencylens" || bundle.version !== BACKUP_VERSION) {
    throw new DriveBackupError(
      `Unrecognised backup format (app=${(bundle as { app?: string }).app ?? "?"}, version=${(bundle as { version?: number }).version ?? "?"}).`,
    );
  }
  return bundle;
}

export async function deleteBackup(
  accessToken: string,
  fileId: string,
): Promise<void> {
  const res = await fetch(
    `https://www.googleapis.com/drive/v3/files/${fileId}`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  if (!res.ok && res.status !== 204) {
    const text = await res.text().catch(() => "");
    throw new DriveBackupError(
      `DELETE /drive/v3/files/{id}: ${res.status} ${text.slice(0, 200)}`,
      res.status,
    );
  }
}

/* -------------------------------------------------- formatting helpers */

function toBackupFile(f: DriveFile): BackupFile {
  return {
    id: f.id,
    name: f.name,
    ...(f.createdTime ? { createdAt: f.createdTime } : {}),
    ...(f.modifiedTime ? { modifiedAt: f.modifiedTime } : {}),
    ...(f.size ? { sizeBytes: Number(f.size) } : {}),
  };
}

function defaultBackupName(d: Date = new Date()): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `agencylens-backup-${y}${m}${day}-${hh}${mm}.json`;
}

/* -------------------------------------------------- type-narrowing helpers
 *
 * Exported separately so the SPA can introspect a downloaded bundle
 * before restoring. */

export function isBackupBundle(value: unknown): value is BackupBundle {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Partial<BackupBundle>;
  return (
    v.app === "agencylens" &&
    v.version === BACKUP_VERSION &&
    typeof v.createdAt === "string" &&
    typeof v.records === "object" &&
    v.records !== null
  );
}

/**
 * Helper for SPA's restore flow: extract connections (records with the
 * prefix `connections:`) from a bundle.
 */
export function extractConnectionRecords(
  bundle: BackupBundle,
): Record<string, { accessToken: EncryptedBlob; refreshToken?: EncryptedBlob }> {
  const out: Record<string, { accessToken: EncryptedBlob; refreshToken?: EncryptedBlob }> = {};
  for (const [key, value] of Object.entries(bundle.records)) {
    if (!key.startsWith("connections:")) continue;
    out[key] = value as { accessToken: EncryptedBlob; refreshToken?: EncryptedBlob };
  }
  return out;
}
